import { IAdminPatientListItem } from "../../../@types/admin/AdminPatient";
import { useSendEmedRegistration } from "../../../hooks/mutations/admin/patients";
import { useModal } from "../../../hooks/useModal";
import { useToast } from "../../../hooks/useToast";
import { errorToastMessage } from "../../../utils/HttpError";
import {
  FilterButton,
  ModalBodyText,
  ModalContainer,
  ModalForm,
  ModalFormActions,
  ModalTitle,
} from "../shared";

interface ISendEmedRegistrationModalProps {
  patient: IAdminPatientListItem;
}

/**
 * Confirmation for registering a patient on the eMED integration (EN-5169).
 *
 * Retool asked with its own generic `confirmationMessage` ("Are you sure you
 * want to register the patient on eMED integration?") — which never named
 * which patient, even though the action was fired from a row. This names them.
 */
export function SendEmedRegistrationModal({
  patient,
}: ISendEmedRegistrationModalProps) {
  const { closeModal } = useModal();
  const { showToast } = useToast();
  const { sendEmedRegistration, isSendingEmed } = useSendEmedRegistration();

  const handleSend = async () => {
    try {
      await sendEmedRegistration({ patientId: patient.id });
      // Retool's own success text, kept.
      showToast(
        "The patient was successfully registered on eMED integration.",
        "success"
      );
      closeModal();
    } catch (error) {
      showToast(
        errorToastMessage(
          error,
          "There was an error registering this patient on the eMED integration."
        ),
        "warning"
      );
    }
  };

  return (
    <ModalContainer>
      <ModalTitle>Register on eMED integration</ModalTitle>
      <ModalForm>
        <ModalBodyText>
          Register <strong>{patient.name}</strong> (patient ID {patient.id}) on
          the eMED integration? This sends their record to eMED and starts the
          medication feed for them.
        </ModalBodyText>
        <ModalFormActions>
          <FilterButton
            variant="secondary"
            disabled={isSendingEmed}
            onClick={closeModal}
          >
            Cancel
          </FilterButton>
          <FilterButton
            variant="primary"
            disabled={isSendingEmed}
            onClick={handleSend}
          >
            {isSendingEmed ? "Registering..." : "Register"}
          </FilterButton>
        </ModalFormActions>
      </ModalForm>
    </ModalContainer>
  );
}
